import {sendAnalyticsEvent} from '#/analytics/analytics';
import {TransactionData} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {transactionTypeConstants} from '#/components/transaction/transaction-type-constants';
import {
  getContractTransactionCustomError,
  getContractTransactionErrorStringForAnalytics,
} from '#/utils/error-message-utils';
import {wagmiConfig} from '#/wagmi/wagmi-config';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Land, LandMarketInfo, TransactionType} from 'shareland-common';
import {BaseError, WriteContractReturnType} from 'viem';
import {waitForTransactionReceipt} from 'wagmi/actions';

type TransactionState =
  | 'not-ready'
  | 'ready'
  | 'confirm-approval'
  | 'approval'
  | 'confirm-operation'
  | 'operation'
  | 'success';

export type TransactionFlow = {
  status: TransactionState;
  hasApproval?: boolean;
  type?: TransactionType;
  error?: Error;
};

type StartTransactionFlow = (
  approvalContractTransactionPromise: (() => Promise<WriteContractReturnType | null>) | null,
  operationContractTransactionPromise: () => Promise<WriteContractReturnType>,
  onSuccess?: () => void,
) => Promise<void>;

type UseTransactionFlow = (params: {
  land: Land;
  landMarketInfo: LandMarketInfo;
  transactionData: TransactionData;
  options?: {notReady?: boolean};
}) => [TransactionFlow, StartTransactionFlow];

export const useTransactionFlow: UseTransactionFlow = ({land, landMarketInfo, transactionData, options}) => {
  const {notReady = false} = options || {};

  // Internal hooks
  const [status, setStatus] = useState<TransactionFlow['status']>(notReady ? 'not-ready' : 'ready');
  const [hasApproval, setHasApproval] = useState<TransactionFlow['hasApproval']>();
  const [error, setError] = useState<TransactionFlow['error']>();

  // Functions
  const startTransactionFlow: StartTransactionFlow = async (
    approvalContractTransactionPromise,
    operationContractTransactionPromise,
    onSuccess,
  ) => {
    const hasApproval = !!approvalContractTransactionPromise;
    setHasApproval(hasApproval);

    sendAnalyticsEvent.transactionStarted({land, landMarketInfo, transactionData});

    try {
      if (hasApproval) {
        setStatus('confirm-approval');
        const approvalWriteContractResult = await approvalContractTransactionPromise();

        if (!!approvalWriteContractResult) {
          setStatus('approval');

          await waitForTransactionReceipt(wagmiConfig, {
            hash: approvalWriteContractResult,
          });
        }
      }

      setStatus('confirm-operation');
      const operationHash = await operationContractTransactionPromise();

      setStatus('operation');
      const operationTransactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash: operationHash,
      });

      if (!operationTransactionReceipt) {
        throw new Error('Empty purchase contract receipt.');
      }

      sendAnalyticsEvent.transactionCompleted({land, landMarketInfo, transactionData});

      setStatus('success');

      onSuccess?.();
    } catch (error) {
      const targetError = getErrorFromCatchBlockParam(error);

      sendAnalyticsEvent.transactionFailed({
        land,
        landMarketInfo,
        transactionData,
        cause: getContractTransactionErrorStringForAnalytics(targetError),
      });
      setStatus('ready');
      setError(targetError);
    }
  };

  return [{status, hasApproval, type: transactionData.type, error}, startTransactionFlow];
};

const getErrorFromCatchBlockParam = (error: unknown): Error | BaseError => {
  if (error instanceof BaseError) {
    return getContractTransactionCustomError(error);
  }

  if (error instanceof Error) return error;

  return new Error('An unknown error occured.');
};

export const useTransactionFlowMessage = (transactionFlow: TransactionFlow): string => {
  const {t} = useTranslation('translation');

  const {status, type, hasApproval} = transactionFlow;

  if (type === undefined || hasApproval === undefined) return '';
  return {
    'not-ready': '',
    'ready': '',
    'confirm-approval': t('pages.actionMenu.transaction.transactionFlowMessage.confirmApproval'),
    'approval': t('pages.actionMenu.transaction.transactionFlowMessage.approval'),
    'confirm-operation': t(
      !hasApproval
        ? 'pages.actionMenu.transaction.transactionFlowMessage.confirmOperation'
        : 'pages.actionMenu.transaction.transactionFlowMessage.confirmOperationSecondStep',
      {
        transactionTypeName: t(`transactions.${transactionTypeConstants[type].i18nKey}.present`),
      },
    ),
    'operation': t(
      !hasApproval
        ? 'pages.actionMenu.transaction.transactionFlowMessage.operation'
        : 'pages.actionMenu.transaction.transactionFlowMessage.operationSecondStep',
      {
        transactionTypeName: t(`transactions.${transactionTypeConstants[type].i18nKey}.present`),
      },
    ),
    'success': '',
  }[status];
};
